// Shared interactive 3D preview for the Sites storefront and Shopify theme.
// Both supplied CAD frames load on demand; returning to a model reuses its mesh.
const FRAME_MODELS = {
  telo8: {name:'GT Telo8', number:'01', file:'telo8-frame'},
  molo5: {name:'Molo5', number:'02', file:'molo5-frame'}
};
class RideModel extends HTMLElement {
  static get observedAttributes() { return ['model', 'zoom-level']; }
  attributeChangedCallback(name, previous, next) {
    if (!this.connected || previous === next) return;
    if (name === 'zoom-level') {
      this.resize();
      this.emit('zoomchange', {percent:this.zoomPercent});
      return;
    }
    if (!FRAME_MODELS[next]) return;
    if (this.ready) this.selectModel(next);
    else if (this.visible) this.load();
  }
  get requestedKey() { return FRAME_MODELS[this.getAttribute('model')] ? this.getAttribute('model') : 'telo8'; }
  get zoomPercent() {
    const value = Number(this.getAttribute('zoom-level') || 100);
    return Number.isFinite(value) ? this.clamp(value, 100, 250) : 100;
  }
  connectedCallback() {
    if (this.connected) return;
    this.connected = true;
    this.visible = false;
    this.ready = false;
    this.frame = 0;
    this.controls = new AbortController();
    this.modelCache = new Map();
    this.activeKey = null;
    this.loadingKey = null;
    this.initializing = false;
    this.setAttribute('aria-busy', 'true');
    this.root = this.shadowRoot || this.attachShadow({mode: 'open'});
    this.root.innerHTML = `<style>:host{display:block;position:relative}canvas,svg{display:block;width:100%;height:100%;touch-action:none;cursor:grab;outline-offset:-5px}canvas:active,svg:active{cursor:grabbing}canvas:focus-visible,svg:focus-visible{outline:2px solid #d5f542}.message[hidden]{display:none}.message{z-index:1;pointer-events:none;position:absolute;inset:0;display:grid;place-items:center;font:14px/1.6 Arial,sans-serif;color:#c0c9d0;text-align:center;padding:25px} .render-note{position:absolute;bottom:3px;left:12px;font:12px/1.5 Arial,sans-serif;color:#b8c1c8;pointer-events:none}</style><span class="message" role="status">Loading GT Telo8 frame…</span>`;
    this.bindNativeControls();
    this.intersection = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      if (this.visible) this.ready ? this.schedule() : this.load();
      else { cancelAnimationFrame(this.frame); this.frame = 0; }
    }, {rootMargin: '160px'});
    this.intersection.observe(this);
  }

  emit(name, detail) { this.dispatchEvent(new CustomEvent(name, {detail})); }
  setBusy(loading) {
    this.setAttribute('aria-busy', String(loading));
    if (this.canvas) this.canvas.style.opacity = loading && this.activeKey ? '0.35' : '1';
    this.emit('framestatus', {loading});
  }
  bindNativeControls() {
    if (!this.hasAttribute('data-native-controls')) return;
    const section = this.closest('.model-preview');
    const next = section?.querySelector('[data-frame-next]');
    if (!next) return;
    const options = {signal:this.controls.signal};
    const status = section.querySelector('[data-frame-error]');
    const zoomRange = section.querySelector('[data-model-zoom]');
    const zoomIn = section.querySelector('[data-zoom-in]');
    const zoomOut = section.querySelector('[data-zoom-out]');
    const zoomValue = section.querySelector('[data-zoom-value]');
    const refreshZoom = () => {
      if (!zoomRange) return;
      const percent = this.zoomPercent;
      const busy = this.getAttribute('aria-busy') === 'true';
      zoomRange.value = String(percent);
      zoomRange.setAttribute('aria-valuetext', `${percent}%`);
      zoomRange.style.setProperty('--zoom-progress', `${(percent-100)/1.5}%`);
      zoomRange.disabled = busy;
      zoomIn.disabled = busy || percent >= 250;
      zoomOut.disabled = busy || percent <= 100;
      zoomValue.textContent = `${percent}%`;
    };
    zoomRange?.addEventListener('input', () => this.setAttribute('zoom-level', zoomRange.value), options);
    zoomIn?.addEventListener('click', () => this.setAttribute('zoom-level', String(Math.min(250, this.zoomPercent + 25))), options);
    zoomOut?.addEventListener('click', () => this.setAttribute('zoom-level', String(Math.max(100, this.zoomPercent - 25))), options);
    this.addEventListener('zoomchange', refreshZoom, options);
    this.addEventListener('framestatus', refreshZoom, options);
    refreshZoom();
    const refresh = key => {
      const following = key === 'telo8' ? 'molo5' : 'telo8';
      section.querySelector('[data-frame-current]').textContent = FRAME_MODELS[key].name;
      section.querySelector('[data-frame-number]').textContent = FRAME_MODELS[key].number;
      next.querySelector('[data-frame-next-name]').textContent = FRAME_MODELS[following].name;
      next.setAttribute('aria-label', `Next model: ${FRAME_MODELS[following].name}`);
    };
    next.addEventListener('click', () => {
      if (status) status.hidden = true;
      this.setAttribute('model', this.activeKey === 'molo5' ? 'telo8' : 'molo5');
    }, options);
    this.addEventListener('framechange', event => {
      refresh(event.detail.key);
      if (status) status.hidden = true;
    }, options);
    this.addEventListener('framestatus', event => { next.disabled = event.detail.loading; }, options);
    this.addEventListener('frameerror', event => {
      if (status) {status.hidden = false; status.textContent = `Could not load ${event.detail.name}. Please try again.`;}
      this.setAttribute('model', this.activeKey || 'telo8');
    }, options);
    refresh(this.requestedKey);
  }
  async load() {
    if (this.initializing || this.ready) return;
    this.initializing = true;
    const connection = this.controls;
    try {
      const THREE = await import('./three.module.min.js');
      if (connection.signal.aborted) return;
      if (!await this.initialize(THREE)) return;
      if (connection.signal.aborted) return;
      this.ready = true;
      this.resize();
      await this.selectModel(this.requestedKey);
    } catch (error) {
      if (!connection.signal.aborted) {
        this.setBusy(false);
        this.root.querySelector('.message').textContent = 'The 3D frame could not load. Please refresh to try again.';
        this.emit('frameerror', {name:FRAME_MODELS[this.requestedKey].name, activeKey:this.activeKey});
        console.warn('3D preview unavailable', error);
      }
    } finally { if (this.controls === connection) this.initializing = false; }
  }
  async selectModel(key) {
    if (!this.ready || key === this.activeKey || key === this.loadingKey || !FRAME_MODELS[key]) return;
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    this.loadingKey = key;
    this.setBusy(true);
    const message = this.root.querySelector('.message');
    message.textContent = `Loading ${FRAME_MODELS[key].name} frame…`;
    message.hidden = false;
    try {
      let entry = this.modelCache.get(key);
      if (!entry) {
        const suffix = this.vectorRenderer ? '-lite.glb' : '.glb';
        const source = new URL(`./${FRAME_MODELS[key].file}${suffix}`, import.meta.url);
        const response = await fetch(source, {signal:request.signal});
        if (!response.ok) throw new Error('Frame model request failed');
        const gltf = await new this.GLTFLoader().parseAsync(await response.arrayBuffer(), new URL('.', source).href);
        if (request.signal.aborted || !this.connected) {this.disposeObjects([gltf.scene]); return;}
        const T = this.THREE;
        const assembly = gltf.scene;
        assembly.traverse(object => {
          if (!object.isMesh) return;
          object.castShadow = true;
          object.receiveShadow = true;
          const original = object.material;
          if (this.vectorRenderer) {
            object.material = new T.MeshLambertMaterial({color:original.color, side:T.FrontSide, flatShading:true});
            original.dispose();
          } else {original.metalness = 0.25; original.roughness = 0.48;}
        });
        const bounds = new T.Box3().setFromObject(assembly);
        const size = bounds.getSize(new T.Vector3());
        const center = bounds.getCenter(new T.Vector3());
        const scale = 3.8 / Math.max(size.x, size.y, size.z);
        const normalized = new T.Group();
        assembly.position.sub(center);
        normalized.add(assembly);
        normalized.scale.setScalar(scale);
        entry = {object:normalized, size:size.multiplyScalar(scale)};
        this.modelCache.set(key, entry);
      }
      if (request.signal.aborted || !this.connected) return;
      this.frameGroup.clear();
      this.frameGroup.add(entry.object);
      this.modelSize = entry.size;
      if (this.floor) this.floor.position.y = -this.modelSize.y / 2 - 0.07;
      this.target = {x:0.04, y:-0.48};
      this.frameGroup.rotation.set(this.target.x, this.target.y, 0);
      this.activeKey = key;
      this.setAttribute('data-active-model', key);
      this.setAttribute('aria-label', `Interactive ${FRAME_MODELS[key].name} frame model`);
      this.canvas.setAttribute('aria-label', `${FRAME_MODELS[key].name} frame 3D model. Drag or use arrow keys to rotate. Home resets the view.`);
      message.hidden = true;
      this.loadingKey = null;
      this.request = null;
      this.setBusy(false);
      this.resize();
      this.emit('framechange', {key});
    } catch (error) {
      if (request.signal.aborted || !this.connected) return;
      this.loadingKey = null;
      this.request = null;
      this.setBusy(false);
      message.hidden = Boolean(this.activeKey);
      if (!this.activeKey) message.textContent = 'The 3D frame could not load. Please refresh to try again.';
      this.emit('frameerror', {name:FRAME_MODELS[key].name, activeKey:this.activeKey});
      console.warn('Frame model unavailable', error);
    }
  }

  async initialize(T) {
    this.THREE = T;
    const surface = document.createElement('canvas');
    const context = surface.getContext('webgl2', {alpha:true, antialias:true, powerPreference:'low-power'});
    this.vectorRenderer = !context;
    if (context) {
      this.renderer = new T.WebGLRenderer({canvas:surface, context, alpha:true, antialias:true});
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.renderer.toneMapping = T.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.15;
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = T.PCFSoftShadowMap;
    } else {
      const {SVGRenderer} = await import('./SVGRenderer.js');
      if (!this.connected) return false;
      this.renderer = new SVGRenderer();
      this.renderer.setQuality('high');
      this.renderer.setPrecision(2);
      this.renderer.setClearColor(0x25292d);
    }
    this.scene = new T.Scene();
    this.camera = new T.PerspectiveCamera(36, 1, 0.1, 40);
    this.camera.position.set(0, 0.6, 5.6);
    this.camera.lookAt(0, 0, 0);
    this.scene.add(new T.HemisphereLight(0xf3f7ff, 0x707782, 2.8));
    if (this.vectorRenderer) this.scene.add(new T.AmbientLight(0xffffff, 1.1));
    const key = new T.DirectionalLight(0xffffff, this.vectorRenderer ? 2.4 : 4.2);
    key.position.set(-3, 6, 4);
    key.castShadow = true;
    key.shadow.mapSize.set(512, 512);
    key.shadow.camera.left = -4; key.shadow.camera.right = 4;
    key.shadow.camera.top = 4; key.shadow.camera.bottom = -4;
    key.shadow.normalBias = 0.03;
    this.scene.add(key);
    const rim = new T.DirectionalLight(0xe5efff, this.vectorRenderer ? 1.6 : 3);
    rim.position.set(3, 3, -3); this.scene.add(rim);
    const fill = new T.DirectionalLight(0xffffff, this.vectorRenderer ? 1.2 : 1.8);
    fill.position.set(2, 1, 5); this.scene.add(fill);
    this.frameGroup = new T.Group(); this.scene.add(this.frameGroup);
    const {GLTFLoader} = await import('./GLTFLoader.js');
    if (!this.connected) return false;
    this.GLTFLoader = GLTFLoader;
    if (!this.vectorRenderer) {
      this.floor = new T.Mesh(new T.PlaneGeometry(12, 12), new T.ShadowMaterial({opacity:0.17}));
      this.floor.rotation.x = -Math.PI / 2;
      this.floor.receiveShadow = true;
      this.scene.add(this.floor);
    }
    this.target = {x:0.04, y:-0.48};
    this.frameGroup.rotation.set(this.target.x, this.target.y, 0);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.canvas = this.renderer.domElement;
    this.canvas.setAttribute('tabindex', '0');
    this.canvas.setAttribute('role', 'img');
    this.canvas.setAttribute('aria-label', 'GT Telo8 frame 3D model. Drag or use arrow keys to rotate. Home resets the view.');
    this.root.append(this.canvas);
    if (this.vectorRenderer) {
      const hint = document.createElement('span');
      hint.className = 'render-note';
      hint.textContent = 'Simplified view';
      this.root.append(hint);
    }
    const options = {signal:this.controls.signal};
    this.canvas.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.drag = {id:event.pointerId, x:event.clientX, y:event.clientY, rx:this.target.x, ry:this.target.y};
      this.canvas.setPointerCapture(event.pointerId);
    }, options);
    this.canvas.addEventListener('pointermove', event => {
      if (this.drag && event.pointerId === this.drag.id) {
        this.target.y = this.drag.ry + (event.clientX - this.drag.x) * 0.012;
        this.target.x = this.clamp(this.drag.rx + (event.clientY - this.drag.y) * 0.007, -0.45, 0.5);
      } else if (event.pointerType === 'mouse') {
        const box = this.canvas.getBoundingClientRect();
        this.target.y = -0.48 + ((event.clientX-box.left)/box.width - 0.5) * 2.4;
        this.target.x = ((event.clientY-box.top)/box.height - 0.5) * 0.6;
      } else return;
      this.schedule();
    }, options);
    const endDrag = event => {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      this.drag = null;
      if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
    };
    this.canvas.addEventListener('pointerup', endDrag, options);
    this.canvas.addEventListener('pointercancel', endDrag, options);
    this.canvas.addEventListener('lostpointercapture', () => {this.drag = null;}, options);
    this.canvas.addEventListener('pointerleave', event => {
      if (event.pointerType !== 'mouse' || this.drag) return;
      this.target = {x:0.04,y:-0.48}; this.schedule();
    }, options);
    this.canvas.addEventListener('keydown', event => {
      if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') {this.target = {x:0.04,y:-0.48}; this.setAttribute('zoom-level', '100');}
      if (event.key === 'ArrowLeft') this.target.y -= 0.25;
      if (event.key === 'ArrowRight') this.target.y += 0.25;
      if (event.key === 'ArrowUp') this.target.x = this.clamp(this.target.x-0.15,-0.45,0.5);
      if (event.key === 'ArrowDown') this.target.x = this.clamp(this.target.x+0.15,-0.45,0.5);
      this.schedule();
    }, options);
    this.sizeObserver = new ResizeObserver(() => this.resize());
    this.sizeObserver.observe(this);
    return true;
  }

  clamp(value, min, max) { return Math.min(max, Math.max(min, value)); }
  resize() {
    if (!this.renderer) return;
    const width = this.clientWidth, height = this.clientHeight;
    if (!width || !height) return;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.zoom = this.zoomPercent / 100;
    this.camera.position.z = Math.max(5.6, (this.modelSize?.y || 0) * 2.65, 7.6 / this.camera.aspect);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
    this.schedule();
  }
  schedule() {
    if (!this.ready || !this.visible || this.frame) return;
    this.frame = requestAnimationFrame(() => this.draw());
  }
  draw() {
    this.frame = 0;
    if (!this.ready || !this.visible) return;
    const ease = this.reducedMotion ? 1 : 0.16;
    this.frameGroup.rotation.x += (this.target.x-this.frameGroup.rotation.x)*ease;
    this.frameGroup.rotation.y += (this.target.y-this.frameGroup.rotation.y)*ease;
    this.renderer.render(this.scene, this.camera);
    // Reveal the shared engineering grid behind the software-rendered mesh.
    if (this.vectorRenderer) this.canvas.style.backgroundColor = 'transparent';
    if (Math.abs(this.target.x-this.frameGroup.rotation.x)+Math.abs(this.target.y-this.frameGroup.rotation.y)>0.002) this.schedule();
  }
  disposeObjects(roots) {
    const geometries = new Set(), materials = new Set();
    for (const root of roots) root?.traverse(object => {
      if (object.geometry) geometries.add(object.geometry);
      for (const material of Array.isArray(object.material) ? object.material : [object.material]) if (material) materials.add(material);
    });
    geometries.forEach(geometry => geometry.dispose());
    materials.forEach(material => material.dispose());
  }
  disconnectedCallback() {
    this.connected = false;
    this.ready = false;
    cancelAnimationFrame(this.frame); this.frame = 0;
    this.intersection?.disconnect(); this.sizeObserver?.disconnect(); this.controls?.abort(); this.request?.abort();
    this.disposeObjects([this.scene, ...Array.from(this.modelCache?.values() || [], entry => entry.object)]);
    this.modelCache?.clear();
    this.renderer?.dispose?.(); this.renderer = null; this.scene = null; this.floor = null; this.drag = null;
  }
}
if (!customElements.get('ride-model')) customElements.define('ride-model', RideModel);
